import { NgStyle } from '@angular/common'
import { Component, inject } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faX } from '@fortawesome/free-solid-svg-icons'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import imageCompression, { Options } from 'browser-image-compression'
import { Observable } from 'rxjs'

import { FeatureType, JobHazardAnalysis, ListItem, RepositoryModal, SafetyFeedback } from '../../shared'

@Component({
    templateUrl: './safety-feedback.component.html',
    standalone: true,
    imports: [FormsModule, WjInputModule, FaIconComponent, NgStyle],
})
export class SafetyFeedbackComponent extends RepositoryModal<SafetyFeedback> {
    private router = inject(Router)

    faX = faX
    jobList: ListItem<string>[] = []
    userList: ListItem<string>[] = []
    jhaList: ListItem<string>[] = []
    images: any[] = []
    progress = 0
    isCreatingJha = false

    protected onInitialized(): void {
        const params = this.editOptions.initializeParams as {
            activeJobList: ListItem<string>[]
            activeUserList: ListItem<string>[]
            jhaList: ListItem<string>[]
            currentUserId: string
        }
        this.jobList = params.activeJobList ?? []
        this.userList = params.activeUserList ?? []
        this.jhaList = params.jhaList ?? []

        if (this.isAdding && !this.target.employeeUserId) {
            this.target.employeeUserId = params.currentUserId
        }
    }

    // Managing the JHA link is a safety-manager (Edit) action; field users adding a record don't see it.
    get canManageJha(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyFeedbackEdit])
    }

    // Creating a JHA requires JHA edit rights (the create call is authorized against that feature).
    get canCreateJha(): boolean {
        return this.services.authentication.hasFeature([FeatureType.SafetyJobHazardAnalysisEdit])
    }

    get jhaStatusLabel(): string {
        if (!this.target.requiresJha) return ''
        if (this.target.linkedJhaStatus === 'Submitted') return 'Addressed'
        if (this.target.linkedJobHazardAnalysisId && this.target.linkedJhaStatus) return 'JHA in progress'
        return 'Needs JHA'
    }

    get jhaStatusClass(): string {
        switch (this.jhaStatusLabel) {
            case 'Addressed':
                return 'bg-success'
            case 'JHA in progress':
                return 'bg-info text-dark'
            case 'Needs JHA':
                return 'bg-warning text-dark'
            default:
                return 'bg-secondary'
        }
    }

    removeImage(image) {
        this.images.remove(image)
    }

    async onFileChanged(event) {
        const files = event.target.files
        const progress = (p: number) => {
            this.progress = p
        }
        for (const file of files) {
            const options: Options = {
                maxSizeMB: 2,
                maxWidthOrHeight: 1920,
                useWebWorker: true,
                onProgress: progress,
            }
            const compressedFile = await imageCompression(file, options)
            compressedFile['url'] = await imageCompression.getDataUrlFromFile(compressedFile)
            this.images.push(compressedFile)
        }
    }

    private uploadImages(id: string): Observable<any> {
        const uploadData = new FormData()
        uploadData.append('id', id)
        for (let i = 0; i < this.images.length; i++) {
            uploadData.append('myFile' + i, this.images[i], this.images[i].name)
        }
        return this.services.data.post('api/SafetyFeedback/UploadImages', uploadData)
    }

    protected onSave(resolveSave: (result?: any) => void, rejectSave: (reason?: any) => void) {
        const observable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
        observable.subscribe({
            next: successResult => {
                if (this.images.length === 0) {
                    resolveSave(successResult)
                    return
                }

                this.uploadImages(successResult.id).subscribe({
                    next: () => resolveSave(successResult),
                    error: errorResult => rejectSave(errorResult),
                })
            },
            error: errorResult => rejectSave(errorResult),
        })
    }

    // Creates a draft JHA pre-filled from this feedback, links it, saves the feedback, then opens the JHA.
    createJha() {
        this.target.requiresJha = true
        this.isCreatingJha = true

        const jha = JobHazardAnalysis.createNew()
        jha.job = this.target.job ?? ''
        jha.description = this.target.eventDescription ?? ''
        if (this.target.eventDateTime) {
            jha.date = new Date(this.target.eventDateTime as any)
        }

        this.services.data.jobHazardAnalyses.add(jha).subscribe({
            next: savedJha => {
                this.target.linkedJobHazardAnalysisId = savedJha.id
                const saveObservable = this.isAdding || this.isDuplicating ? this.repositoryAdd() : this.repositoryEdit()
                saveObservable.subscribe({
                    next: savedFeedback => {
                        const finish = () => {
                            this.activeModal.close(savedFeedback)
                            this.router.navigate(['/safety/job-hazard-analysis'], { queryParams: { openJhaId: savedJha.id } })
                        }

                        if (this.images.length === 0) {
                            finish()
                            return
                        }

                        this.uploadImages(savedFeedback.id).subscribe({
                            next: () => finish(),
                            error: errorResult => {
                                this.isCreatingJha = false
                                this.onError(errorResult)
                            },
                        })
                    },
                    error: errorResult => {
                        this.isCreatingJha = false
                        this.onError(errorResult)
                    },
                })
            },
            error: errorResult => {
                this.isCreatingJha = false
                this.onError(errorResult)
            },
        })
    }

    openLinkedJha() {
        const id = this.target.linkedJobHazardAnalysisId
        this.activeModal.dismiss('open-jha')
        this.router.navigate(['/safety/job-hazard-analysis'], { queryParams: { openJhaId: id } })
    }
}
